// Entry module for the main SPA (loaded by index.html as /app.js). It owns
// the bootstrap SIDE EFFECTS — service-worker registration, the global
// dblclick-selects-all tweak, the header pill / theme toggle, the multitab
// stale overlay, and the initial cloud-availability probe + first render.
// All shared singletons and the render/navigate/refresh dispatch live in the
// side-effect-free `core.js`, which page modules (and the divorced public
// viewer, via the grid renderer) import without dragging in this bootstrap.
import { el } from "./lib/dom.js";
import { getMode, setMode, loadCloudAvailability } from "./local/mode.js";
import { mountHeaderPill } from "./local/header-pill.js";
import { mountThemeToggle, applyEarly as applyEarlyTheme } from "./lib/theme.js";
import { mountResetAppButton } from "./lib/reset-app.js";
import { setupBroadcast } from "./local/multitab.js";
import { initServiceWorker, onUpdateAvailable } from "./lib/register-sw.js";
import { showUpdateToast } from "./lib/update-toast.js";
import { maybeShowWelcome } from "./lib/welcome.js";
import { maybeWarnAccessBypass } from "./lib/access-warning.js";
import { isNetworkError } from "./lib/net.js";
// === DEMO SYSTEM (delete this import + the mountDemo() call below, the
// setDemoLaunchSuppressed() calls in renderError()'s offline branch, the
// frontend/demo/ folder, and the demo CSS block in index.html to remove) ===
import { mountDemo, setDemoLaunchSuppressed } from "./demo/index.js";
import { state, refresh, render, prefetchCurrentRoute, navigate } from "./core.js";
import { brandLink } from "./lib/brand.js";
// Re-export the shared API singleton + refresh so e2e specs (and ad-hoc
// console debugging) can drive the same in-page api the UI uses via
// `import("/app.js")` — the in-browser handleApi shim in local mode, or the
// network in cloud mode. See tests/e2e/local-mode.spec.js + automations.spec.js.
export { api, refresh } from "./core.js";

// Service worker precaches the SPA shell so local mode keeps working
// with no network. Idempotent — safe to call before anything else
// renders; the SW takes over asynchronously.
initServiceWorker();

// When a fresh deploy takes over (the SW claims the page with new code while
// this tab still runs the old /app.js), offer a reload rather than silently
// running stale code. Fires at most once; the toast is dismissable.
onUpdateAvailable(showUpdateToast);

window.addEventListener("popstate", () => { prefetchCurrentRoute(); render(); });

// Global UX tweak: double-click in any <input> or <textarea> selects all the
// existing value. The native browser default is to select a word, which is
// rarely what people want when about to overwrite a field. Done as a single
// delegated listener at the document level so every form input picks it up
// without per-component wiring.
document.addEventListener("dblclick", (e) => {
  const t = e.target;
  if (!t || (t.tagName !== "INPUT" && t.tagName !== "TEXTAREA")) return;
  if (typeof t.select === "function") t.select();
});

// Mount the always-visible Cloud / Local pill. Switching mode wipes the
// in-memory `state` (schemas / validators / relationships / grids are
// bound to the active store) and re-renders from the new transport.
// Apply the persisted theme choice before the next paint, then mount
// the toggle pill. `applyEarly` only writes the data-theme attribute;
// the actual recolouring is driven by CSS in public/index.html.
applyEarlyTheme();
mountThemeToggle();
mountResetAppButton();
mountDemo(); // DEMO SYSTEM — shows by default unless DEMO_DISABLED (see import above)
mountHeaderPill(async () => {
  state.schemas = []; state.validators = []; state.relationships = []; state.views = [];
  // Pop the user back to the home route on mode change so they aren't
  // staring at a saved-grid id that exists in the other store.
  if (location.pathname !== "/") history.pushState({}, "", "/");
  clearStale();
  await refresh().catch(renderError);
});

// Stale overlay + auto-refresh on focus: another tab wrote to the local DB
// while we were idle, so dim the page and refresh() immediately if focused,
// or on the next `visibilitychange` if hidden — clickable in the meantime
// for an on-demand refresh. Held off while an editor is focused so it can't
// nuke an in-progress edit.
let staleOverlay = null;
let staleRefreshing = false;

function editorActive() {
  const a = document.activeElement;
  if (!a) return false;
  if (a.tagName === "INPUT" || a.tagName === "TEXTAREA") return true;
  return !!a.isContentEditable;
}

async function refreshFromStale() {
  if (staleRefreshing) return;
  staleRefreshing = true;
  try {
    clearStale();
    await refresh().catch(renderError);
  } finally {
    staleRefreshing = false;
  }
}

function showStale() {
  if (staleOverlay) return;
  staleOverlay = el("div", {
    class: "data-stale-overlay",
    title: "Click to refresh",
    onclick: () => { refreshFromStale(); },
  },
    el("div", { class: "data-stale-msg" },
      el("strong", {}, "Updated in another tab"),
      el("span", {}, "Click to refresh"),
    ),
  );
  document.body.append(staleOverlay);
  document.body.classList.add("data-stale");

  // Try to auto-refresh now if the user is plausibly looking at us and
  // not editing. Otherwise the visibilitychange listener below will
  // pick it up on the next focus.
  if (document.visibilityState === "visible" && !editorActive()) {
    refreshFromStale();
  }
}

function clearStale() {
  if (staleOverlay) { staleOverlay.remove(); staleOverlay = null; }
  document.body.classList.remove("data-stale");
}

document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible" && staleOverlay && !editorActive()) {
    refreshFromStale();
  }
});

setupBroadcast({
  onWrote: () => {
    // Only show the stale prompt in local mode. Cloud-mode tabs share
    // the channel name but the cloud Worker isn't broadcasting writes,
    // so in practice this only fires from another local-mode tab.
    if (getMode() !== "local") return;
    showStale();
  },
});

function renderError(e) {
  const root = document.getElementById("root");
  if (getMode() === "cloud" && isNetworkError(e)) {
    // Cloud storage is unreachable (no connectivity). Don't dump a raw
    // fetch error over a blank page — keep the brand and offer the
    // on-device store, which needs no network. Retry re-probes the cloud.
    root.innerHTML = "";
    // The demo launcher auto-anchors next to ANY .home-brand/.brand element
    // it finds (see frontend/demo/index.js's position()) — including the
    // brandLink() below, which would otherwise invite the user to start an
    // interactive demo while the app can't even reach the server. Cleared
    // by the two actions below the moment they attempt to leave this state,
    // regardless of whether that attempt succeeds (a failed retry just
    // re-enters this same branch and re-suppresses).
    setDemoLaunchSuppressed(true);
    const box = el("div", { class: "local-locked-banner offline-banner" },
      brandLink(navigate),
      el("h2", {}, "You're offline"),
      el("p", {},
        "csvdb can't reach server storage right now — the network looks unavailable. ",
        "Your data is safe; nothing was lost. Switch to ",
        el("strong", {}, "local SQLite"),
        " to keep working entirely on this device with no connection, or retry once you're back online.",
      ),
      el("div", { class: "local-locked-actions" },
        el("button", { class: "button accent", onclick: async () => {
          setDemoLaunchSuppressed(false);
          setMode("local");
          state.schemas = []; state.validators = []; state.relationships = []; state.views = [];
          if (location.pathname !== "/") history.pushState({}, "", "/");
          await refresh().catch((err) => renderError(err));
        } }, "Switch to local"),
        el("button", { class: "button button-outline", onclick: () => { setDemoLaunchSuppressed(false); refresh().catch(renderError); } }, "Retry"),
      ),
    );
    root.append(box);
    return;
  }
  if (e && e.code === "OPFS_LOCKED") {
    // Reached only when the auto-grab reclaim in multitab.js exhausted
    // its retry budget — typically because another tab is
    // browser-suspended and isn't responding to release-request
    // broadcasts. Tell the user honestly; suggest the two recovery
    // paths.
    root.innerHTML = "";
    const box = el("div", { class: "local-locked-banner" },
      el("h2", {}, "Another tab isn't responding"),
      el("p", {},
        "csvdb's local database is held by another tab on ",
        el("code", {}, location.host),
        ", and that tab didn't release it when asked (it may be suspended in the background). Reload or close that tab, then click Retry. Or switch to server mode to keep working.",
      ),
      el("div", { class: "local-locked-actions" },
        el("button", { class: "button accent", onclick: () => { location.reload(); } }, "Retry"),
        el("button", { class: "button button-outline", onclick: async () => {
          setMode("cloud");
          state.schemas = []; state.validators = []; state.relationships = []; state.views = [];
          await refresh().catch((err) => renderError(err));
        } }, "Switch to server"),
      ),
    );
    root.append(box);
    return;
  }
  root.innerHTML = `<pre class="err">${(e && e.message) || String(e)}</pre>`;
}

// Probe whether the Worker has a D1 binding before the first refresh.
// When it doesn't, `getMode()` flips to "local" and the picker hides the
// Cloud option, so the SPA never tries cloud routes that would 503.
loadCloudAvailability().finally(() => {
  // Fire the grid data prefetch in parallel with refresh() so a cold
  // deep-link to /view/<id> doesn't serialize refresh → metadata → rows.
  prefetchCurrentRoute();
  refresh().catch(renderError);
  maybeShowWelcome();
  // Warn the admin if Cloudflare Access is intercepting the public `/~/` path
  // (published views would be unreachable without a bypass rule). Probes the
  // network, so keep it off the critical render path.
  maybeWarnAccessBypass();
  // Offline write-outbox: wire the reconnect listeners and check for a
  // backlog left over from a previous session (see checkOutboxOnBoot's
  // comment for why the boot-time check is needed even though the
  // listeners alone cover a mid-session connectivity drop). Lazy-imported
  // so a session that's always online never pulls in the sqlite-wasm/OPFS
  // machinery the outbox is built on.
  if (getMode() === "cloud") {
    import("./local/sync.js").then(({ startSyncListeners, checkOutboxOnBoot }) => {
      startSyncListeners();
      checkOutboxOnBoot();
    });
  }
});
